import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Edit2, Trash2, X, FileDown, Search, Package, ClipboardList, Eye, Filter } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["DRAFT", "SENT", "RECEIVED", "CANCELLED"];
const STATUS_META = {
  DRAFT:     { label: "Draft",     bg: "bg-zinc-500",  row: "bg-zinc-50" },
  SENT:      { label: "Sent",      bg: "bg-[#1A4DFF]", row: "bg-[#E7EEFF]" },
  RECEIVED:  { label: "Received",  bg: "bg-[#22A06B]", row: "bg-[#DFF3E8]" },
  CANCELLED: { label: "Cancelled", bg: "bg-[#FF3333]", row: "bg-[#FFE0E0]" },
};
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

const todayIST = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return d.toISOString().slice(0, 10);
};

export default function PurchaseOrders() {
  const [tab, setTab] = useState("ALL");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [vendorFilter, setVendorFilter] = useState(""); // clicked-vendor filter
  const [detail, setDetail] = useState(null); // PO detail modal

  async function load() {
    const params = {};
    if (tab !== "ALL") params.status = tab;
    if (search) params.search = search;
    if (vendorFilter) params.vendor = vendorFilter;
    try {
      const [a, b] = await Promise.all([
        api.get("/purchase-orders", { params }),
        api.get("/purchase-orders/vendors"),
      ]);
      setItems(a.data);
      setVendors(b.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  useEffect(() => { load(); }, [tab, search, vendorFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => {
    const c = { ALL: items.length };
    STATUSES.forEach((s) => { c[s] = 0; });
    items.forEach((it) => { if (STATUSES.includes(it.status)) c[it.status] += 1; });
    return c;
  }, [items]);

  const totalAmount = useMemo(() => items.reduce((a, p) => a + (Number(p.total) || 0), 0), [items]);
  const totalUnits = useMemo(() =>
    items.reduce((a, p) => a + (p.items || []).reduce((x, it) => x + (Number(it.quantity) || 0), 0), 0)
  , [items]);

  async function onDelete(po) {
    if (!window.confirm(`Delete PO "${po.po_number}"?`)) return;
    try { await api.delete(`/purchase-orders/${po.id}`); toast.success("PO deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  function downloadExcel(poId) {
    const params = new URLSearchParams();
    if (poId) params.set("po_id", poId);
    else if (tab !== "ALL") params.set("status", tab);
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const url = `${BACKEND}/api/exports/purchase-orders.xlsx${params.toString() ? "?" + params.toString() : ""}`;
    window.open(url, "_blank");
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="po-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2 inline-flex items-center gap-2">
            <ClipboardList size={12} /> / PROCUREMENT · PURCHASE ORDERS
          </div>
          <h1 className="section-title text-5xl lg:text-6xl">Purchase Orders.</h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-xl">Create POs for vendors (Dutees, Behraji, etc). Track status, download Excel per PO or in bulk.</p>
        </div>
        <div className="flex flex-col gap-2 self-start">
          <button onClick={() => { setEditing(null); setOpen(true); }} className="brut-btn px-5 py-3 inline-flex items-center gap-2" data-testid="add-po-btn">
            <Plus size={16} /> New PO
          </button>
          <button onClick={() => downloadExcel(null)} className="brut-btn-outline px-5 py-2.5 inline-flex items-center gap-2" data-testid="download-all-pos-btn">
            <FileDown size={16} /> All POs Excel
          </button>
        </div>
      </header>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        <TabBtn id="ALL" active={tab} setTab={setTab} label="All" count={counts.ALL} />
        {STATUSES.map((s) => (
          <TabBtn key={s} id={s} active={tab} setTab={setTab} label={STATUS_META[s].label} count={counts[s]} bg={STATUS_META[s].bg} />
        ))}
      </div>

      {/* Summary + vendor chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="PO Count" value={items.length} />
        <Stat label="Total Units" value={totalUnits} />
        <Stat label="Total Value" value={`₹${Math.round(totalAmount).toLocaleString("en-IN")}`} accent />
        <Stat label="Unique Vendors" value={vendors.length} />
      </div>

      {vendors.length > 0 && (
        <div className="brut-card p-3 mb-4 flex flex-wrap items-center gap-2">
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mr-1">
            {vendorFilter ? "Filtering by vendor" : "Click a vendor to filter"}
          </div>
          {vendors.slice(0, 12).map((v) => {
            const isActive = vendorFilter === v.vendor;
            return (
              <button
                key={v.vendor}
                type="button"
                onClick={() => setVendorFilter(isActive ? "" : v.vendor)}
                data-testid={`po-vendor-chip-${v.vendor.replace(/\s+/g, "-")}`}
                className={`px-2.5 py-1 border-2 border-black font-mono-ds text-[10px] font-bold uppercase transition-all ${
                  isActive
                    ? "bg-black text-white shadow-[3px_3px_0_0_#0a0a0a]"
                    : "bg-white hover:bg-zinc-100"
                }`}
              >
                {v.vendor} <span className="opacity-60">({v.count})</span>
              </button>
            );
          })}
          {vendorFilter && (
            <button
              type="button"
              onClick={() => setVendorFilter("")}
              className="ml-auto brut-btn-outline px-3 py-1 inline-flex items-center gap-1.5 text-[10px] font-mono-ds uppercase"
              data-testid="po-clear-vendor-filter"
            >
              <X size={11} /> Clear filter
            </button>
          )}
        </div>
      )}

      {vendorFilter && (
        <div className="mb-3 inline-flex items-center gap-2 px-3 py-2 border-2 border-black bg-[#7A3E1F] text-white font-mono-ds text-[11px] tracking-widest uppercase" data-testid="po-active-vendor-badge">
          <Filter size={12} /> Vendor: <span className="font-black">{vendorFilter}</span>
          <button onClick={() => setVendorFilter("")} className="ml-2 hover:opacity-70"><X size={12} /></button>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-5 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search PO#, vendor, notes…" className="brut-input pl-10" data-testid="po-search" />
      </div>

      {/* POs table */}
      <div className="brut-card overflow-x-auto">
        <table className="brut-table min-w-[1000px]">
          <thead>
            <tr>
              <th>PO #</th>
              <th>Date</th>
              <th>Vendor</th>
              <th>Items</th>
              <th className="min-w-[140px]">Total Units</th>
              <th>Status</th>
              <th className="w-36">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((po) => {
              const units = (po.items || []).reduce((a, it) => a + (Number(it.quantity) || 0), 0);
              return (
                <tr
                  key={po.id}
                  className={`${STATUS_META[po.status]?.row || ""} cursor-pointer hover:bg-[#FDF6EE] transition-colors`}
                  data-testid={`po-row-${po.id}`}
                  onClick={() => setDetail(po)}
                >
                  <td className="font-mono-ds font-black text-xs">{po.po_number}</td>
                  <td className="font-mono-ds text-xs whitespace-nowrap">{po.date}</td>
                  <td className="font-display font-bold">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setVendorFilter(po.vendor); }}
                      className="underline decoration-2 underline-offset-4 hover:text-[#7A3E1F] transition-colors text-left"
                      data-testid={`po-vendor-link-${po.id}`}
                      title={`Filter POs by ${po.vendor}`}
                    >
                      {po.vendor}
                    </button>
                  </td>
                  <td className="font-mono-ds text-xs">{(po.items || []).length}</td>
                  <td className="font-mono-ds font-black text-lg whitespace-nowrap">{units}<span className="opacity-50 text-xs ml-1">tees</span></td>
                  <td>
                    <span className={`brut-badge ${STATUS_META[po.status]?.bg} text-white`} style={{ borderColor: "#0a0a0a" }}>
                      {STATUS_META[po.status]?.label || po.status}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <button onClick={() => setDetail(po)} className="p-1.5 border-2 border-black hover:bg-zinc-100" title="View details" data-testid={`po-view-${po.id}`}><Eye size={12} /></button>
                      <button onClick={() => downloadExcel(po.id)} className="p-1.5 border-2 border-black hover:bg-zinc-100" title="Download this PO as Excel" data-testid={`po-download-${po.id}`}><FileDown size={12} /></button>
                      <button onClick={() => { setEditing(po); setOpen(true); }} className="p-1.5 border-2 border-black hover:bg-zinc-100" data-testid={`po-edit-${po.id}`}><Edit2 size={12} /></button>
                      <button onClick={() => onDelete(po)} className="p-1.5 border-2 border-black hover:bg-[#FF3333] hover:text-white" data-testid={`po-delete-${po.id}`}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-zinc-500 font-mono-ds text-xs uppercase">
                <Package className="inline mr-2" size={14} /> No POs yet · Click &quot;New PO&quot; to create the first one
              </td></tr>
            )}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              <tr className="bg-black text-white font-display font-black" data-testid="po-totals-row">
                <td colSpan={4} className="text-right pr-4 font-mono-ds text-[11px] tracking-widest uppercase">TOTAL →</td>
                <td className="font-mono-ds font-black text-lg" data-testid="po-total-units">{totalUnits}<span className="opacity-50 text-xs ml-1">tees</span></td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {open && <POForm po={editing} vendors={vendors} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {detail && <PODetailModal po={detail} onClose={() => setDetail(null)} onEdit={() => { setEditing(detail); setDetail(null); setOpen(true); }} onFilterVendor={() => { setVendorFilter(detail.vendor); setDetail(null); }} onDownload={() => downloadExcel(detail.id)} />}
    </div>
  );
}

function PODetailModal({ po, onClose, onEdit, onFilterVendor, onDownload }) {
  const units = (po.items || []).reduce((a, it) => a + (Number(it.quantity) || 0), 0);
  const meta = STATUS_META[po.status] || { label: po.status, bg: "bg-zinc-500" };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="po-detail-modal">
      <div className="brut-card w-full max-w-3xl bg-white">
        <div className={`flex items-center justify-between p-5 border-b-2 border-black ${meta.bg} text-white`}>
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-80">PURCHASE ORDER · {meta.label}</div>
            <h2 className="font-display font-black text-2xl tracking-tight">{po.po_number}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/20" data-testid="close-po-detail"><X size={20} /></button>
        </div>

        <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4 border-b-2 border-black">
          <DetailStat label="Date" value={po.date} />
          <DetailStat label="Vendor" value={
            <button onClick={onFilterVendor} className="underline decoration-2 underline-offset-4 hover:text-[#7A3E1F]" data-testid="po-detail-vendor-link">
              {po.vendor}
            </button>
          } />
          <DetailStat label="Line Items" value={(po.items || []).length} />
          <DetailStat label="Total Units" value={<span>{units}<span className="opacity-50 text-xs"> u</span></span>} />
          <DetailStat label="Total Units" value={<span className="text-2xl">{units}<span className="opacity-50 text-sm"> tees</span></span>} accent />
          <DetailStat label="Payment Terms" value={po.payment_terms || "—"} />
          <DetailStat label="Expected On" value={po.expected_date || "—"} />
          <DetailStat label="Placed by" value={po.user_name || "—"} />
        </div>

        <div className="p-6 border-b-2 border-black">
          <div className="font-mono-ds text-[11px] tracking-widest uppercase text-zinc-600 mb-3">Items ({(po.items || []).length})</div>
          <div className="overflow-x-auto">
            <table className="brut-table min-w-[520px]">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th>Color</th>
                  <th>Size</th>
                  <th className="min-w-[110px]">Qty</th>
                </tr>
              </thead>
              <tbody>
                {(po.items || []).map((it, i) => (
                  <tr key={i} data-testid={`po-detail-item-${i}`}>
                    <td className="font-mono-ds text-xs">{i + 1}</td>
                    <td className="font-display font-bold">{it.product_name || "—"}</td>
                    <td>{it.color || "—"}</td>
                    <td className="font-mono-ds font-bold">{it.size || "—"}</td>
                    <td className="font-mono-ds font-black text-lg whitespace-nowrap">{it.quantity || 0}<span className="opacity-50 text-xs ml-1">tees</span></td>
                  </tr>
                ))}
                {(!po.items || po.items.length === 0) && (
                  <tr><td colSpan={5} className="text-center py-6 text-zinc-500 font-mono-ds text-xs uppercase">No line items on this PO</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {po.notes && (
          <div className="p-6 border-b-2 border-black">
            <div className="font-mono-ds text-[11px] tracking-widest uppercase text-zinc-600 mb-2">Notes</div>
            <div className="text-sm whitespace-pre-wrap">{po.notes}</div>
          </div>
        )}

        <div className="p-5 flex flex-wrap justify-end gap-2">
          <button onClick={onFilterVendor} className="brut-btn-outline px-4 py-2 inline-flex items-center gap-2 text-sm" data-testid="po-detail-filter-vendor-btn">
            <Filter size={13} /> Filter by {po.vendor}
          </button>
          <button onClick={onDownload} className="brut-btn-outline px-4 py-2 inline-flex items-center gap-2 text-sm" data-testid="po-detail-download-btn">
            <FileDown size={13} /> Download Excel
          </button>
          <button onClick={onEdit} className="brut-btn px-4 py-2 inline-flex items-center gap-2 text-sm" data-testid="po-detail-edit-btn">
            <Edit2 size={13} /> Edit PO
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailStat({ label, value, accent = false }) {
  return (
    <div className={`brut-card-flat p-3 ${accent ? "bg-black text-white" : ""}`}>
      <div className={`font-mono-ds text-[10px] tracking-widest uppercase ${accent ? "opacity-70" : "text-zinc-500"}`}>{label}</div>
      <div className="font-display font-black text-lg break-words">{value}</div>
    </div>
  );
}

function TabBtn({ id, active, setTab, label, count, bg }) {
  const isActive = id === active;
  return (
    <button onClick={() => setTab(id)} data-testid={`po-tab-${id.toLowerCase()}`}
      className={`px-4 py-2.5 border-2 border-black font-display font-black text-xs uppercase tracking-wider transition-all ${
        isActive ? (bg ? `${bg} text-white` : "bg-black text-white") + " shadow-[4px_4px_0_0_#0a0a0a]" : "bg-white text-black hover:bg-zinc-100"
      }`}>
      {label} <span className="ml-1 font-mono-ds opacity-75">({count})</span>
    </button>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`brut-card p-4 ${accent ? "bg-black text-white" : ""}`}>
      <div className={`font-mono-ds text-[10px] tracking-widest uppercase mb-1 ${accent ? "opacity-70" : "text-zinc-500"}`}>{label}</div>
      <div className="font-display font-black text-2xl">{value}</div>
    </div>
  );
}

function POForm({ po, vendors, onClose, onSaved }) {
  const isEdit = !!po;
  const [form, setForm] = useState(() => ({
    po_number: po?.po_number || "",
    vendor: po?.vendor || "",
    date: po?.date || todayIST(),
    status: po?.status || "DRAFT",
    notes: po?.notes || "",
    items: po?.items?.length ? po.items.map((it) => ({ ...it })) : [emptyLine()],
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  function setItem(idx, k, v) {
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)) }));
  }
  function addLine() { setForm((f) => ({ ...f, items: [...f.items, emptyLine()] })); }
  function removeLine(idx) { setForm((f) => ({ ...f, items: f.items.length === 1 ? f.items : f.items.filter((_, i) => i !== idx) })); }

  const total = useMemo(() =>
    form.items.reduce((a, it) => a + (Number(it.quantity) || 0) * (Number(it.rate) || 0), 0)
  , [form.items]);
  const units = useMemo(() => form.items.reduce((a, it) => a + (Number(it.quantity) || 0), 0), [form.items]);

  async function save() {
    if (!form.vendor.trim()) { toast.error("Vendor is required"); return; }
    if (!form.items.length || !form.items.some((it) => (Number(it.quantity) || 0) > 0)) {
      toast.error("Add at least one line with qty > 0"); return;
    }
    setBusy(true);
    try {
      const payload = {
        po_number: form.po_number || undefined,
        vendor: form.vendor,
        date: form.date,
        status: form.status,
        notes: form.notes,
        items: form.items.map((it) => ({
          product_name: it.product_name || "",
          color: it.color || "",
          size: it.size || "",
          quantity: Number(it.quantity) || 0,
          rate: Number(it.rate) || 0,
        })).filter((it) => it.quantity > 0 || it.product_name || it.rate > 0),
      };
      if (isEdit) await api.put(`/purchase-orders/${po.id}`, payload);
      else await api.post("/purchase-orders", payload);
      toast.success(isEdit ? "PO updated" : "PO created");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-6 px-4" data-testid="po-form-modal">
      <div className="brut-card w-full max-w-5xl bg-white">
        <div className={`flex items-center justify-between p-5 border-b-2 border-black ${STATUS_META[form.status]?.bg || "bg-black"} text-white`}>
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-80">{isEdit ? "EDIT" : "NEW"} PO · {STATUS_META[form.status]?.label}</div>
            <h2 className="font-display font-black text-2xl">{isEdit ? `Edit ${po.po_number}` : "New Purchase Order"}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/20" data-testid="close-po-form"><X size={20} /></button>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label>PO Number (auto if blank)</Label>
            <input className="brut-input font-mono-ds" value={form.po_number} onChange={(e) => set("po_number", e.target.value)} placeholder="PO-2026..." data-testid="po-number-input" />
          </div>
          <div>
            <Label>Vendor *</Label>
            <input className="brut-input" list="po-vendor-list" value={form.vendor} onChange={(e) => set("vendor", e.target.value)} placeholder="Dutees, Behraji…" data-testid="po-vendor-input" />
            <datalist id="po-vendor-list">
              {vendors.map((v) => <option key={v.vendor} value={v.vendor} />)}
            </datalist>
          </div>
          <div>
            <Label>Date</Label>
            <input type="date" className="brut-input font-mono-ds" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="po-date-input" />
          </div>
          <div>
            <Label>Status</Label>
            <select className="brut-input" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="po-status-select">
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
            </select>
          </div>
        </div>

        <div className="px-6">
          <Label>Line Items</Label>
          <div className="brut-card overflow-x-auto">
            <table className="brut-table min-w-[600px]">
              <thead>
                <tr>
                  <th className="w-8">#</th>
                  <th>Product</th>
                  <th>Color</th>
                  <th>Size</th>
                  <th className="w-32">Quantity</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => (
                  <tr key={idx} data-testid={`po-line-${idx}`}>
                    <td className="font-mono-ds text-xs text-zinc-400">{idx + 1}</td>
                    <td><input className="brut-input" value={it.product_name} onChange={(e) => setItem(idx, "product_name", e.target.value)} placeholder="Cotton oversize" data-testid={`po-line-product-${idx}`} /></td>
                    <td><input className="brut-input" value={it.color} onChange={(e) => setItem(idx, "color", e.target.value)} placeholder="Black" data-testid={`po-line-color-${idx}`} /></td>
                    <td>
                      <select className="brut-input" value={it.size} onChange={(e) => setItem(idx, "size", e.target.value)} data-testid={`po-line-size-${idx}`}>
                        <option value="">—</option>
                        {SIZES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="brut-input font-mono-ds font-black text-lg text-center"
                        value={it.quantity ?? ""}
                        onChange={(e) => setItem(idx, "quantity", e.target.value.replace(/[^0-9]/g, ""))}
                        placeholder="0"
                        data-testid={`po-line-qty-${idx}`}
                      />
                    </td>
                    <td>
                      <button onClick={() => removeLine(idx)} disabled={form.items.length === 1} className="p-1 border-2 border-black hover:bg-[#FF3333] hover:text-white disabled:opacity-40" data-testid={`po-line-remove-${idx}`}>
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={addLine} className="mt-2 brut-btn-outline px-3 py-1.5 inline-flex items-center gap-2" data-testid="po-add-line">
            <Plus size={14} /> Add Line
          </button>
        </div>

        <div className="p-6">
          <Label>Notes</Label>
          <textarea rows={2} className="brut-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Delivery instructions, expected date etc." data-testid="po-notes-input" />
        </div>

        <div className="border-t-2 border-black p-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="bg-black text-white px-6 py-3 flex items-center gap-6 border-2 border-black shadow-[4px_4px_0_0_#0a0a0a]" data-testid="po-form-totals">
            <div>
              <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-70">Lines</div>
              <div className="font-display font-black text-2xl leading-none">{form.items.filter((it) => (Number(it.quantity) || 0) > 0).length}</div>
            </div>
            <div className="w-px h-10 bg-white/30" />
            <div>
              <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-70">Total Units</div>
              <div className="font-display font-black text-3xl leading-none" data-testid="po-form-total-units">{units}<span className="opacity-50 text-base ml-1">tees</span></div>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={onClose} className="brut-btn-outline px-5 py-2.5" data-testid="cancel-po-btn">Cancel</button>
            <button onClick={save} disabled={busy} className="brut-btn px-5 py-2.5 disabled:opacity-60" data-testid="save-po-btn">
              {busy ? "Saving…" : (isEdit ? "Save Changes" : "Create PO")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">{children}</div>;
}
function emptyLine() { return { product_name: "", color: "", size: "", quantity: 0, rate: 0 }; }
